import { useState } from "react";
import { twMerge } from "tailwind-merge";

import { TUser } from "@/types";

export type UserAvatarProps = {
    user: TUser | null;
    className?: string;
}

/**
 * The signed-in applicant's own picture. `profileImage` is the only image the
 * API puts on the user (`UserDTO.profileImage`) and it is often absent, so the
 * fallback is the user's initials — never a stock photo of somebody else.
 */
function initialsOf(user: TUser | null) {
    const letters = [user?.firstName, user?.lastName]
        .map((name) => name?.trim()?.charAt(0))
        .filter(Boolean)
        .join("");

    if (letters) return letters.toUpperCase();

    return user?.email?.trim()?.charAt(0)?.toUpperCase() ?? "?";
}

const UserAvatar = ({ user, className }: UserAvatarProps) => {
    // A stored URL can 404 once the bucket link expires; falling back keeps a
    // broken-image icon out of the top bar.
    const [failed, setFailed] = useState(false);

    const shape = twMerge(
        "w-8 h-8 shrink-0 rounded-full object-cover",
        className
    );

    if (user?.profileImage && !failed) {
        return (
            <img
                src={user.profileImage}
                alt={`${user.firstName ?? ""} ${user.lastName ?? ""}`.trim() || "Profile picture"}
                onError={() => setFailed(true)}
                className={shape}
            />
        );
    }

    return (
        <span
            aria-hidden="true"
            className={twMerge(
                shape,
                "inline-flex justify-center items-center bg-primary-50 text-primary text-xs font-semibold"
            )}
        >
            {initialsOf(user)}
        </span>
    );
};

export default UserAvatar;
